import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Box, Button } from '@mui/material';
import { useParams } from 'react-router-dom';
import ModalDialog from 'src/components/ModalDialog';
import { useForm } from 'src/components/Form';
import { useAlert } from 'src/components/AlertsProvider';
import { useStoreUnitActions } from 'src/redux/helpers';
import solutionsStore, {
  useAssetMappingAdx,
  useSolutionAdxMappedTables,
  useSolutionMappingFileDestinationOptions,
  useSolutionMappingFileOptions,
} from '../../redux';
import { mappingConfigurationFormFields } from './SolutionMappingEditFormFields';
import {
  SolutionMappingConfigItem,
  SolutionMappingConfigType,
} from '../../types';
import { defaultOptions } from '../constant';

const containerStyle = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  overflowY: 'auto',
  alignItems: 'center',
  padding: '10px',
};

type Props = {
  item?: SolutionMappingConfigItem;
  title: string;
  loading?: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (values: SolutionMappingConfigItem) => void;
  confirmBtnText?: string;
};

function SolutionMappingEditModal({
  item,
  title,
  loading,
  open,
  onClose,
  onSubmit,
  confirmBtnText = 'Create',
}: Props) {
  const actions = useStoreUnitActions(solutionsStore);
  const { showAlert } = useAlert();
  const { solutionId } = useParams();

  const [formValues, setFormValues] = useState<SolutionMappingConfigItem>(
    item || {
      id: '',
      sourceType: '',
      destinationType: '',
      path: '',
      mappings: [],
      solutionId: '',
      siteId: '',
      twinId: '',
      tableName: '',
    }
  );

  const [options, optionLoading] = useSolutionMappingFileOptions();
  const [destinationOptionsRaw, loadingDestinationOptions] =
    useSolutionMappingFileDestinationOptions();
  const [adxTablesData, loadingAdxTables] = useSolutionAdxMappedTables();
  const [adxColumnsDataRaw, loadingAdxColumns] = useAssetMappingAdx();

  const destinationOptions = useMemo(() => {
    if (!formValues.path) {
      return [];
    }
    return destinationOptionsRaw || [];
  }, [formValues.path, destinationOptionsRaw]);

  const adxColumnsData = useMemo(() => {
    if (!formValues.tableName || formValues.tableName === 'none') {
      return [];
    }
    return adxColumnsDataRaw || [];
  }, [formValues.tableName, adxColumnsDataRaw]);

  const onChange = useCallback((values: SolutionMappingConfigItem) => {
    console.log('Form onChange called with values:', values);
    console.log('Field values:', {
      sourceType: values.sourceType,
      destinationType: values.destinationType,
      tableName: values.tableName,
      path: values.path,
    });
    setFormValues((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(values)) {
        return prev;
      }
      return values;
    });
  }, []);
  
  useEffect(() => {
    if (open) {
      setFormValues(
        item || {
          id: '',
          sourceType: '',
          destinationType: '',
          path: '',
          mappings: [],
          solutionId: '',
          siteId: '',
          twinId: '',
          tableName: '',
        }
      );
      
      if (!item?.tableName && solutionId) {
        actions.assetMappingAdx({
          solutionId,
          tableName: null,
        });
      }
      
      if (!item?.path) {
        actions.assetMappingFileDestination(null);
      }
    }
  }, [open, item, solutionId]);

  useEffect(() => {
    if (solutionId) actions.adxMappedTables(solutionId);
  }, [solutionId]);

  useEffect(() => {
    if (formValues.destinationType === SolutionMappingConfigType.Omniverse) {
      actions.assetMappingFilePath();
    }
  }, [formValues.destinationType]);

  useEffect(() => {
    if (formValues.tableName && formValues.tableName !== 'none' && solutionId) {
      actions.assetMappingAdx({
        solutionId,
        tableName: formValues.tableName,
      });
    }
  }, [formValues.tableName, solutionId]);

  useEffect(() => {
    if (formValues.path) {
      actions.assetMappingFileDestination(formValues.path);
    }
  }, [formValues.path]);

  const adxOptions = useMemo(() => {
    if (Array.isArray(adxTablesData) && adxTablesData.length > 0) {
      return adxTablesData.map((table: any) => ({
        label: table.tableName,
        value: table.tableName,
      }));
    }
    return [];
  }, [adxTablesData]);

  const adxTableNames = useMemo(
    () => [...defaultOptions, ...adxOptions],
    [adxOptions]
  );

  const fields = useMemo(
    () =>
      mappingConfigurationFormFields(
        !!item,
        formValues.destinationType,
        formValues.path,
        options,
        optionLoading,
        destinationOptions,
        loadingDestinationOptions,
        adxTableNames,
        loadingAdxTables,
        formValues.sourceType,
        formValues.tableName,
        adxColumnsData,
        loadingAdxColumns
      ),
    [
      item,
      formValues,
      options,
      optionLoading,
      destinationOptions,
      loadingDestinationOptions,
      adxTableNames,
      loadingAdxTables,
      adxColumnsData,
      loadingAdxColumns,
    ]
  );

  const [form, handleSubmit] = useForm({
    visibleValuesOnly: true,
    fields,
    values: item,
    onSubmit,
    onChange,
    sx: containerStyle,
  });

  return (
    <ModalDialog
      open={open}
      title={title}
      confirmBtnText={confirmBtnText}
      loading={loading}
      onClose={onClose}
      maxWidth="md"
      titleStyle={{ height: '67px' }}
      actionsStyle={{ height: '87px' }}
      showCancelBtn={false}
      showConfirmBtn={false}
      contentStyle={{ paddingRight: '0px' }}
      fullWidth
    >
      {form}
      <Box textAlign="right" mt={3} style={{ paddingRight: '20px' }}>
        <Button onClick={onClose} variant="outlined" sx={{ mr: 2 }}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained">
          {!item ? 'Create' : 'Save'}
        </Button>
      </Box>
    </ModalDialog>
  );
}

export default SolutionMappingEditModal;